/**
 * Item Page
 * Create and edit collection items
 */

import { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from '@/components/ui/card';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
    ArrowLeft,
    Save,
    Trash2,
    Loader2,
    Check,
    X,
    Archive,
    ArchiveRestore,
} from 'lucide-react';
import { useItem, useCreateItem, useUpdateItem, useDeleteItem } from '@/hooks/useItems';
import { useFields, type Field } from '@/hooks/useFields';
import { useCollection } from '@/hooks/useCollections';
import { useRelations } from '@/hooks/useRelations';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { expandUserFields } from '@/utils/user-fields';
import { api } from '@/lib/api';
import { updateField as updateFieldApi } from '@/api/fields';
import { useMyPermissions } from '@/hooks/access-control';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from '@/components/ui/tooltip';

// Interface components
import { MarkdownEditor } from '@/components/interfaces/MarkdownEditor';
import { MultiSelectDropdown } from '@/components/interfaces/MultiSelectDropdown';
import { DateTimePicker } from '@/components/interfaces/DateTimePicker';
import { M2ORelationSelector } from '@/components/interfaces/M2ORelationSelector';
import { M2MRelationInterface } from '@/components/interfaces/M2MRelationInterface';
import { RadioGroup } from '@/components/interfaces/RadioGroup';
import { PresentationLinks } from '@/components/interfaces/PresentationLinks';
import { CheckboxInterface } from '@/components/interfaces/CheckboxInterface';
import { FileInterface } from '@/components/interfaces/FileInterface';
import { UrlFieldRenderer } from '@/components/interfaces/UrlFieldRenderer';

// Sidebar hook and detail components
import { useRightSidebar, type SidebarSection } from '@/components/sidebar/index';
import { InfoSidebarDetail } from '@/components/sidebar/details/InfoSidebarDetail';
import { RevisionsSidebarDetail } from '@/components/sidebar/details/RevisionsSidebarDetail';
import { CommentsSidebarDetail } from '@/components/sidebar/details/CommentsSidebarDetail';
import { Info, History, MessageSquare } from 'lucide-react';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { useComments } from '@/hooks/useComments';
import { useItemRevisionsCount } from '@/hooks/useRevisions';

// Dynamic form data type
type ItemFormData = Record<string, unknown>;

export function ItemPage() {
    const { collection, id } = useParams<{ collection: string; id: string }>();
    const navigate = useNavigate();
    const { data: collectionInfo } = useCollection(collection || '');
    const isSingleton = collectionInfo?.meta?.singleton;
    const effectiveId = isSingleton ? 'singleton' : id;
    const isNew = effectiveId === '+';
    const currentItemId = isSingleton ? 'singleton' : id;

    // Fetch counts for sidebar badges
    const { commentsCount } = useComments(collection || '', isNew ? '' : (currentItemId || ''));
    const { data: revisionsCount } = useItemRevisionsCount(collection || '', isNew ? '' : (currentItemId || ''));

    // Local state
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [hasChanges, setHasChanges] = useState(false);
    const [formData, setFormData] = useState<ItemFormData>({});
    const [originalData, setOriginalData] = useState<ItemFormData>({});
    const [customSelectValues, setCustomSelectValues] = useState<Record<string, string>>({});
    const [userCache, setUserCache] = useState<Record<string, Record<string, any>>>({});

    // Fetch collection info
    // Fetch fields (include hidden so we can control visibility with toggle)
    const { fields, loading: fieldsLoading } = useFields({
        collection: collection || null,
        includeHidden: true
    });

    // Fetch all relations (needed for M2M which stores relations on junction collection)
    const { data: relations } = useRelations();
    const { data: myPermissions } = useMyPermissions();

    // Fetch item if editing
    const itemFields = useMemo(() => {
        if (fields.length === 0) return ['*'];
        return ['*', ...expandUserFields(fields.map(f => f.field), fields)];
    }, [fields]);

    const { data: item, isLoading: itemLoading } = useItem(
        collection || '',
        isSingleton ? 'singleton' : (isNew ? '' : (id || '')),
        { fields: itemFields }
    );

    // Mutations
    const createItem = useCreateItem(collection || '');
    const updateItem = useUpdateItem(collection || '');
    const deleteItem = useDeleteItem(collection || '');

    // Get primary key field
    const primaryKeyField = useMemo(() => {
        return fields.find(f => f.schema?.is_primary_key)?.field || 'id';
    }, [fields]);

    const systemManagedSpecials = useMemo(() => ['user-created', 'user-updated', 'date-created', 'date-updated'], []);

    const collectionPermissions = useMemo(
        () => (myPermissions || []).filter((p) => p.collection === collection),
        [myPermissions, collection]
    );
    const canCreate = collectionPermissions.some((p) => p.action === 'create' && p.access !== 'none');
    const canUpdate = collectionPermissions.some((p) => p.action === 'update' && p.access !== 'none');
    const canDelete = collectionPermissions.some((p) => p.action === 'delete' && p.access !== 'none');

    const archiveField = collectionInfo?.meta?.archive_field;
    const archiveValue = collectionInfo?.meta?.archive_value ?? 'archived';
    const unarchiveValue = collectionInfo?.meta?.unarchive_value ?? null;
    const archiveAppFilter = collectionInfo?.meta?.archive_app_filter;
    const hasArchive = useMemo(() => Boolean(archiveField && archiveAppFilter), [archiveField, archiveAppFilter]);
    const readonlyByPermission = isNew ? !canCreate : !canUpdate;
    const isArchived = useMemo(() => {
        if (!hasArchive || !archiveField) return false;
        return (formData[archiveField] ?? null) === archiveValue;
    }, [archiveField, archiveValue, formData, hasArchive]);

    const getSpecials = useCallback((field: Field): string[] => {
        const specials = field.meta?.special;
        if (!specials) return [];
        return Array.isArray(specials)
            ? specials.map((s) => String(s).toLowerCase())
            : [String(specials).toLowerCase()];
    }, []);

    const isSystemManagedField = useCallback((field: Field) => {
        const specials = getSpecials(field);
        return specials.some((s) => systemManagedSpecials.includes(s));
    }, [getSpecials, systemManagedSpecials]);

    const sanitizePayload = useCallback((data: ItemFormData): ItemFormData => {
        const cleaned: ItemFormData = { ...data };

        if (primaryKeyField) {
            delete cleaned[primaryKeyField];
        }

        fields.forEach((field) => {
            if (isSystemManagedField(field)) {
                delete cleaned[field.field];
            }

            // Transform M2M fields to Directus format
            // Detect M2M by interface type OR special array containing 'm2m'
            const fieldValue = cleaned[field.field];
            const specials = getSpecials(field);
            const isM2MField = specials.includes('m2m') || field.meta?.interface === 'list-m2m';

            if (isM2MField && fieldValue != null) {
                // Check if it's already in M2MValue format {create:[], update:[], delete:[]}
                if (
                    typeof fieldValue === 'object' &&
                    !Array.isArray(fieldValue) &&
                    'create' in fieldValue &&
                    'update' in fieldValue &&
                    'delete' in fieldValue
                ) {
                    const m2mValue = fieldValue as { create: unknown[]; update: unknown[]; delete: unknown[] };
                    // Only include if there are actual changes
                    if (m2mValue.create.length > 0 || m2mValue.update.length > 0 || m2mValue.delete.length > 0) {
                        // Keep as-is, it's already in the correct format
                        cleaned[field.field] = m2mValue;
                    } else {
                        // No changes to M2M - remove from payload
                        delete cleaned[field.field];
                    }
                } else if (Array.isArray(fieldValue)) {
                    // Legacy array format with $type markers - convert to M2MValue
                    const createItems: Record<string, unknown>[] = [];
                    const updateItems: Record<string, unknown>[] = [];
                    const deleteItems: (string | number)[] = [];

                    for (const item of fieldValue as Array<Record<string, unknown>>) {
                        const { $type, $index, ...itemData } = item;

                        if ($type === 'created') {
                            createItems.push(itemData);
                        } else if ($type === 'updated') {
                            updateItems.push(itemData);
                        } else if ($type === 'deleted') {
                            // For delete, we need the junction item ID
                            if (itemData.id != null) {
                                deleteItems.push(itemData.id as string | number);
                            }
                        }
                        // If no $type, it's an unchanged existing item - don't include
                    }

                    // Only set the field if there are actual changes
                    if (createItems.length > 0 || updateItems.length > 0 || deleteItems.length > 0) {
                        cleaned[field.field] = {
                            create: createItems,
                            update: updateItems,
                            delete: deleteItems,
                        };
                    } else {
                        // No changes to M2M - remove from payload
                        delete cleaned[field.field];
                    }
                } else {
                    // Unknown format, remove
                    delete cleaned[field.field];
                }
            }
        });

        return cleaned;
    }, [fields, isSystemManagedField, primaryKeyField, getSpecials]);

    // Get editable fields (show readonly as disabled, hide hidden fields)
    const editableFields = useMemo(() => {
        return fields.filter(f => {
            // Always skip hidden fields in create/edit
            if (f.meta?.hidden) return false;
            // Skip auto-increment primary keys for create only
            if (isNew && f.schema?.is_primary_key && f.schema?.has_auto_increment) return false;
            return true;
        }).sort((a, b) => {
            // Sort by the sort order from Fields & Layout
            const aSort = a.meta?.sort ?? 999;
            const bSort = b.meta?.sort ?? 999;
            return aSort - bSort;
        });
    }, [fields, isNew]);

    // Load item data into form
    useEffect(() => {
        if (item && !isNew) {
            setFormData(item as ItemFormData);
            setOriginalData(item as ItemFormData);
            setHasChanges(false);
        } else if (isNew && fields.length > 0) {
            // Initialize form with default values for new items
            const defaultData: ItemFormData = {};
            fields.forEach(field => {
                if (field.schema?.is_primary_key) return;
                if (isSystemManagedField(field)) return;
                if (field.schema?.default_value !== null && field.schema?.default_value !== undefined) {
                    defaultData[field.field] = field.schema.default_value;
                }
            });
            setFormData(defaultData);
            setOriginalData(defaultData);
        }
    }, [item, isNew, fields, isSystemManagedField]);

    // Check if M2M value has changes
    const hasM2MChanges = useCallback((value: unknown): boolean => {
        if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
        const m2mValue = value as { create?: unknown[]; update?: unknown[]; delete?: unknown[] };
        return (
            (Array.isArray(m2mValue.create) && m2mValue.create.length > 0) ||
            (Array.isArray(m2mValue.update) && m2mValue.update.length > 0) ||
            (Array.isArray(m2mValue.delete) && m2mValue.delete.length > 0)
        );
    }, []);

    // Track changes with M2M awareness
    useEffect(() => {
        // First check standard field changes
        let changed = false;

        for (const key of Object.keys(formData)) {
            const formValue = formData[key];
            const origValue = originalData[key];

            // Check if this is an M2M field (has create/update/delete structure)
            if (hasM2MChanges(formValue)) {
                changed = true;
                break;
            }

            // Standard comparison for other fields
            if (JSON.stringify(formValue) !== JSON.stringify(origValue)) {
                changed = true;
                break;
            }
        }

        // Also check if original has keys that form doesn't
        if (!changed) {
            for (const key of Object.keys(originalData)) {
                if (!(key in formData) && originalData[key] !== undefined) {
                    changed = true;
                    break;
                }
            }
        }

        setHasChanges(changed);
    }, [formData, originalData, hasM2MChanges]);

    // Fetch user details for user special fields when only an ID is present
    useEffect(() => {
        const userFields = fields.filter((f) => {
            const specials = Array.isArray(f.meta?.special)
                ? f.meta?.special
                : typeof f.meta?.special === 'string'
                    ? [f.meta?.special]
                    : [];
            return specials.some((s) => String(s).toLowerCase().includes('user'));
        });

        userFields.forEach(async (field) => {
            const val = formData[field.field];
            if (!val || typeof val === 'object') return;
            const key = String(val);
            if (userCache[key]) return;

            const template = field.meta?.options?.template as string | undefined;
            const templateFields = template
                ? Array.from(template.matchAll(/\{\{([^}]+)\}\}/g)).map((m) => m[1]?.trim() || '')
                : [];
            const requiredFields = Array.from(new Set(['id', 'first_name', 'last_name', 'email', 'avatar', ...templateFields]));
            const params = new URLSearchParams();
            requiredFields.forEach((f) => params.append('fields[]', f));

            try {
                const response = await api.get<{ data: Record<string, any> }>(`/users/${key}?${params.toString()}`);
                const user = response.data;
                if (user) {
                    setUserCache((prev) => ({ ...prev, [key]: user }));
                }
            } catch (error) {
                console.error('Failed to fetch user', error);
            }
        });
    }, [fields, formData, userCache]);

    // Update field value
    const updateField = useCallback((fieldName: string, value: unknown) => {
        setFormData(prev => ({ ...prev, [fieldName]: value }));
    }, []);

    /**
     * Add a custom "Other" value as a new choice in the field's meta.options.choices.
     * This persists the new choice to the field settings so it appears for all users.
     */
    const addOtherChoiceToField = useCallback(async (field: Field, newValue: string) => {
        if (!collection || !newValue) return;
        try {
            const existingChoices = (field.meta?.options?.choices as Array<{ text: string; value: string }>) || [];
            // Don't add duplicates
            if (existingChoices.some((c) => c.value === newValue)) return;

            const updatedChoices = [...existingChoices, { text: newValue, value: newValue }];
            await updateFieldApi(collection, field.field, {
                meta: {
                    options: {
                        ...((field.meta?.options as Record<string, unknown>) || {}),
                        choices: updatedChoices,
                    },
                },
            });
            toast.success(`Added "${newValue}" to ${field.name || field.field} options`);
        } catch (error) {
            // Silently fail — the item value is still set, just the field option wasn't persisted
            console.warn('Failed to persist custom choice to field options:', error);
        }
    }, [collection]);

    // Get field value
    const getFieldValue = useCallback((fieldName: string): unknown => {
        return formData[fieldName];
    }, [formData]);

    // Handlers
    const handleBack = () => {
        navigate(`/admin/content/${collection}`);
    };

    const handleDiscard = () => {
        setFormData(originalData);
        setHasChanges(false);
    };

    const handleSubmit = async (e?: React.FormEvent) => {
        e?.preventDefault();

        if (isNew && !canCreate) {
            toast.error('You do not have permission to create items');
            return;
        }

        if (!isNew && !canUpdate) {
            toast.error('You do not have permission to update items');
            return;
        }

        // Validate required fields
        const invalidFields: string[] = [];
        for (const field of fields) {
            if (isSystemManagedField(field)) continue;
            const isRequired = field.meta?.required || field.schema?.is_nullable === false;
            if (isRequired && !field.schema?.is_primary_key) {
                const value = formData[field.field];
                // Check for null, undefined, or empty string
                if (value === null || value === undefined || value === '') {
                    const fieldLabel = field.meta?.note || field.field.replace(/_/g, ' ');
                    invalidFields.push(`${fieldLabel}: Value can't be null`);
                }
            }
        }

        if (invalidFields.length > 0) {
            toast.error(`The following fields have invalid values:\n${invalidFields.join('\n')}`);
            return;
        }

        try {
            if (isSingleton) {
                const payload = sanitizePayload(formData);
                const updated = await updateItem.mutateAsync({ id: 'singleton', data: payload });
                setOriginalData(updated as ItemFormData);
                setHasChanges(false);
                toast.success('Item saved');
                return;
            }

            if (isNew) {
                const payload = sanitizePayload(formData);
                const created = await createItem.mutateAsync(payload);
                setOriginalData(created as ItemFormData);
                setHasChanges(false);
                toast.success('Item created successfully');
                navigate(`/admin/content/${collection}/${created[primaryKeyField] ?? created.id ?? ''}`);
                return;
            }

            const sanitizedCurrent = sanitizePayload(formData);
            const sanitizedOriginal = sanitizePayload(originalData);
            const keys = Array.from(new Set([...Object.keys(sanitizedCurrent), ...Object.keys(sanitizedOriginal)]));

            // Only send changed fields to the API (PATCH behavior)
            const changedData: ItemFormData = {};
            for (const key of keys) {
                if (JSON.stringify(sanitizedCurrent[key]) !== JSON.stringify(sanitizedOriginal[key])) {
                    changedData[key] = sanitizedCurrent[key];
                }
            }

            // Only make API call if there are actual changes
            if (Object.keys(changedData).length === 0) {
                toast.info('No changes to save');
                return;
            }

            const updated = await updateItem.mutateAsync({ id: currentItemId!, data: changedData });
            setOriginalData(updated as ItemFormData);
            setHasChanges(false);
            toast.success('Item updated');
            // Navigate back to the listing page after successful update
            handleBack();
        } catch (error) {
            // Show the actual API error message
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            toast.error(errorMessage);
        }
    };

    const handleArchiveToggle = async (mode: 'archive' | 'unarchive') => {
        if (!archiveField || !collection || !currentItemId) return;
        if (!canUpdate) {
            toast.error('You do not have permission to update this item');
            return;
        }

        const targetValue = mode === 'archive' ? archiveValue : unarchiveValue ?? null;

        try {
            const updated = await updateItem.mutateAsync({ id: currentItemId, data: { [archiveField]: targetValue } });
            setFormData((prev) => ({ ...prev, ...(updated as ItemFormData) }));
            setOriginalData((prev) => ({ ...prev, ...(updated as ItemFormData) }));
            setHasChanges(false);
            toast.success(mode === 'archive' ? 'Item archived' : 'Item restored');
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            toast.error(errorMessage);
        }
    };

    const handleDelete = async () => {
        if (!canDelete) {
            toast.error('You do not have permission to delete items');
            setDeleteDialogOpen(false);
            return;
        }
        try {
            await deleteItem.mutateAsync(currentItemId!);
            toast.success('Item deleted');
            handleBack();
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            toast.error(errorMessage);
        } finally {
            setDeleteDialogOpen(false);
        }
    };

    // Handle revision revert - apply changes to form without saving
    const handleRevisionRevert = (revertData: Record<string, any>) => {
        if (readonlyByPermission) {
            toast.error('You do not have permission to update this item');
            return;
        }

        // Apply the revision data to the current form
        // We need to properly merge the revert data, ensuring all selected fields are updated
        setFormData(prev => {
            const updated = { ...prev };

            // Apply each field from revert data
            for (const [field, value] of Object.entries(revertData)) {
                // Handle null/undefined explicitly
                if (value === null || value === undefined) {
                    updated[field] = null;
                } else {
                    updated[field] = value;
                }
            }

            return updated;
        });

        // Don't update originalData - we want the form to detect these as changes
        // so the save button is enabled

        // The hasChanges will be automatically set to true by the useEffect
        // that watches formData and originalData changes
    };

    // Register sidebar sections for the global right sidebar
    const sidebarSections = useMemo((): SidebarSection[] => {
        if (isNew || !collection || !id) return [];

        return [
            {
                id: `info-${collection}-${id}`,
                icon: <Info className="h-5 w-5" />,
                title: 'Information',
                defaultOpen: true,
                content: (
                    <InfoSidebarDetail
                        title="Information"
                        collectionName={collectionInfo?.meta?.collection || collection}
                        description={collectionInfo?.meta?.note || undefined}
                        icon="database"
                        defaultOpen={true}
                    />
                ),
            },
            {
                id: `revisions-${collection}-${id}`,
                icon: <History className="h-5 w-5" />,
                title: 'Revisions',
                badge: revisionsCount,
                content: (
                    <RevisionsSidebarDetail
                        collection={collection}
                        itemId={id}
                        onRevert={handleRevisionRevert}
                    />
                ),
            },
            {
                id: `comments-${collection}-${id}`,
                icon: <MessageSquare className="h-5 w-5" />,
                title: 'Comments',
                badge: commentsCount,
                content: (
                    <CommentsSidebarDetail
                        collection={collection}
                        itemId={id}
                        defaultOpen={false}
                    />
                ),
            }
        ];
    }, [collection, id, isNew, collectionInfo, handleRevisionRevert, revisionsCount, commentsCount]);

    // Register sidebar sections with global sidebar
    useRightSidebar(sidebarSections);

    // Render field based on type
    const renderField = (field: Field) => {
        const fieldName = field.field;
        const fieldType = field.type?.toLowerCase() || field.schema?.data_type?.toLowerCase() || 'string';
        const interfaceType = field.meta?.interface?.toLowerCase();
        const isRequired = field.meta?.required || !field.schema?.is_nullable;
        // Use field.name which comes from translations or formatted field name
        const label = field.name || fieldName.replace(/_/g, ' ');
        const helperText = field.meta?.note || null;
        const isReadonly = field.meta?.readonly || (field.schema?.is_primary_key && !isNew) || readonlyByPermission;
        const specials = Array.isArray(field.meta?.special)
            ? field.meta?.special
            : typeof field.meta?.special === 'string'
                ? [field.meta.special]
                : [];
        const normalizedSpecials = specials.map(s => String(s).toLowerCase());
        const isUserSpecial = normalizedSpecials.some(s => s.includes('user'));
        const template = field.meta?.options?.template as string | undefined;

        const renderTemplate = (tpl: string, data: Record<string, unknown>): string => {
            return tpl.replace(/\{\{([^}]+)\}\}/g, (_match, key) => {
                const trimmed = String(key).trim();
                const value = trimmed.split('.').reduce<unknown>((acc, part) => {
                    if (acc && typeof acc === 'object' && part in acc) {
                        return (acc as Record<string, unknown>)[part];
                    }
                    return undefined;
                }, data);
                return value == null ? '' : String(value);
            }).trim();
        };

        // Determine width
        const width = field.meta?.width || 'full';
        const widthClass = width === 'half' || width === 'half-left' || width === 'half-right'
            ? 'col-span-1'
            : 'col-span-2';

        const value = getFieldValue(fieldName);

        // Check if this field is a M2O relation
        const fieldRelation = relations?.find(r => r.field === fieldName && r.collection === collection);
        const isM2ORelation = fieldRelation && fieldRelation.related_collection;

        // ===== INTERFACE-SPECIFIC RENDERING (priority over type-based) =====

        // File/Image interface - check this BEFORE M2O relation check
        // because file fields are technically M2O relations to cms_files
        if (interfaceType === 'file' || interfaceType === 'file-image') {
            const folderOption = field.meta?.options?.folder as string | undefined;
            const acceptImagesOnly = interfaceType === 'file-image';
            return (
                <div key={fieldName} className={cn('space-y-2', widthClass)}>
                    <Label htmlFor={fieldName} className="capitalize">
                        {label}
                        {isRequired && <span className="text-destructive ml-1">*</span>}
                    </Label>
                    <FileInterface
                        value={value as string | null}
                        onChange={(val) => updateField(fieldName, val)}
                        folder={folderOption}
                        disabled={isReadonly}
                        accept={acceptImagesOnly ? 'image/*' : undefined}
                    />
                </div>
            );
        }

        // Presentation Links (alias field - no data storage)
        if (interfaceType === 'presentation-links') {
            const links = field.meta?.options?.links as Array<{ label: string; icon?: string; type?: string; url?: string; actionType?: string; flow?: string }> | undefined;
            return (
                <div key={fieldName} className={cn('space-y-2', widthClass)}>
                    <PresentationLinks
                        links={links}
                        disabled={isReadonly}
                        primaryKey={isNew ? '+' : id}
                        itemData={formData}
                    />
                </div>
            );
        }

        // User special fields - render read-only user chip with link
        if (isUserSpecial) {
            const userValue = value as Record<string, any> | null;
            const userId = userValue && typeof userValue === 'object' && 'id' in userValue
                ? (userValue.id as string | number)
                : (value as string | number | null);
            const cacheRecord = userId ? userCache[String(userId)] : undefined;
            const userRecord = (userValue && typeof userValue === 'object') ? userValue : cacheRecord;

            const fullName = userRecord && typeof userRecord === 'object'
                ? `${userRecord.first_name || ''} ${userRecord.last_name || ''}`.trim()
                : '';
            const email = userRecord && typeof userRecord === 'object' ? (userRecord.email as string | undefined) : undefined;
            const templateLabel = template && userRecord && typeof userRecord === 'object'
                ? renderTemplate(template, userRecord)
                : '';
            const displayName = templateLabel || fullName || email || (userId ? String(userId) : '—');
            return (
                <div key={fieldName} className={cn('space-y-2', widthClass)}>
                    <Label htmlFor={fieldName} className="capitalize">
                        {label}
                    </Label>
                    <div className="flex items-center gap-3 rounded-md border bg-muted/50 px-3 py-2">
                        <Avatar className="h-8 w-8">
                            <AvatarFallback>
                                {displayName.substring(0, 2).toUpperCase() || 'US'}
                            </AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                                <span className="text-sm font-medium truncate">{displayName}</span>
                            </div>
                        </div>
                    </div>
                </div>
            );
        }

        // M2M Relation field
        if (interfaceType === 'list-m2m' || normalizedSpecials.includes('m2m')) {
            // For M2M, we need to find the junction table relations
            // The field's meta should have info about the junction, or we find it from relations
            const junctionRelation = relations?.find(
                r => r.meta?.one_field === fieldName &&
                    r.meta?.one_collection === collection &&
                    r.meta?.junction_field
            );

            const junctionCollection = junctionRelation?.collection;
            const junctionFieldCurrent = junctionRelation?.field;
            const junctionFieldRelated = junctionRelation?.meta?.junction_field;

            // Find the related collection from the second junction relation
            const relatedRelation = relations?.find(
                r => r.collection === junctionCollection &&
                    r.field === junctionFieldRelated
            );
            const relatedCollection = relatedRelation?.related_collection;

            if (junctionCollection && junctionFieldCurrent && junctionFieldRelated && relatedCollection) {
                return (
                    <div key={fieldName} className={cn('space-y-2', widthClass)}>
                        <Label htmlFor={fieldName} className="capitalize">
                            {label}
                            {isRequired && <span className="text-destructive ml-1">*</span>}
                        </Label>
                        <M2MRelationInterface
                            value={value as Array<Record<string, unknown>> | null}
                            onChange={(val) => updateField(fieldName, val)}
                            junctionCollection={junctionCollection}
                            relatedCollection={relatedCollection}
                            junctionFieldCurrent={junctionFieldCurrent}
                            junctionFieldRelated={junctionFieldRelated}
                            currentPrimaryKey={isNew ? null : (id as string | number) || null}
                            displayTemplate={field.meta?.options?.template as string | undefined}
                            disabled={isReadonly}
                            enableCreate={field.meta?.options?.enableCreate as boolean ?? true}
                            enableSelect={field.meta?.options?.enableSelect as boolean ?? true}
                            enableLink={field.meta?.options?.enableLink as boolean ?? false}
                            limit={field.meta?.options?.limit as number ?? 15}
                        />
                        {helperText && (
                            <p className="text-sm text-muted-foreground">{helperText}</p>
                        )}
                    </div>
                );
            } else {
                // M2M not properly configured
                return (
                    <div key={fieldName} className={cn('space-y-2', widthClass)}>
                        <Label htmlFor={fieldName} className="capitalize">{label}</Label>
                        <div className="rounded-md border border-dashed p-4 text-sm text-muted-foreground">
                            Many-to-Many relationship not fully configured. Check field and relation settings.
                        </div>
                    </div>
                );
            }
        }

        // M2O Relation field
        if (isM2ORelation || interfaceType === 'select-dropdown-m2o') {
            const relatedCollection = fieldRelation?.related_collection;
            if (relatedCollection) {
                return (
                    <div key={fieldName} className={cn('space-y-2', widthClass)}>
                        <Label htmlFor={fieldName} className="capitalize">
                            {label}
                            {isRequired && <span className="text-destructive ml-1">*</span>}
                        </Label>
                        <M2ORelationSelector
                            value={value as string | number | null}
                            onChange={(val) => updateField(fieldName, val)}
                            relatedCollection={relatedCollection}
                            displayTemplate={field.meta?.options?.template as string | undefined}
                            disabled={isReadonly}
                            enableCreate={field.meta?.options?.enableCreate as boolean ?? true}
                            enableLink={field.meta?.options?.enableLink as boolean ?? false}
                        />
                    </div>
                );
            }
        }

        // Boolean/Checkbox fields - use checkbox with label
        if (fieldType === 'boolean' || interfaceType === 'boolean') {
            const checkboxLabel = (field.meta?.options?.label as string) || 'Enabled';
            return (
                <div key={fieldName} className={cn('space-y-2', widthClass)}>
                    <Label htmlFor={fieldName} className="capitalize">
                        {label}
                    </Label>
                    <CheckboxInterface
                        value={value === true ? true : value === false ? false : null}
                        onChange={(checked) => updateField(fieldName, checked)}
                        label={checkboxLabel}
                        disabled={isReadonly}
                    />
                </div>
            );
        }

        // Radio Buttons
        if (interfaceType === 'select-radio') {
            const choices = (field.meta?.options?.choices as Array<{ text: string; value: string }>) || [];
            return (
                <div key={fieldName} className={cn('space-y-2', widthClass)}>
                    <Label htmlFor={fieldName} className="capitalize">
                        {label}
                        {isRequired && <span className="text-destructive ml-1">*</span>}
                    </Label>
                    {choices.length > 0 ? (
                        <RadioGroup
                            value={value as string | number | null}
                            onChange={(val) => updateField(fieldName, val)}
                            choices={choices}
                            disabled={isReadonly}
                        />
                    ) : (
                        <p className="text-sm text-muted-foreground italic">No choices configured</p>
                    )}
                </div>
            );
        }

        // Multi-select dropdown (check interface first, before JSON type fallback)
        if (interfaceType === 'select-multiple-dropdown') {
            const choices = (field.meta?.options?.choices as Array<{ text: string; value: string }>) || [];
            const allowOther = field.meta?.options?.allowOther as boolean | undefined;
            const allowNone = field.meta?.options?.allowNone as boolean | undefined;
            return (
                <div key={fieldName} className={cn('space-y-2', widthClass)}>
                    <Label htmlFor={fieldName} className="capitalize">
                        {label}
                        {isRequired && <span className="text-destructive ml-1">*</span>}
                    </Label>
                    {choices.length > 0 ? (
                        <MultiSelectDropdown
                            value={Array.isArray(value) ? value as string[] : null}
                            onChange={(val) => updateField(fieldName, val)}
                            choices={choices}
                            allowOther={allowOther}
                            allowNone={allowNone}
                            disabled={isReadonly}
                            onAddOtherChoice={(newVal) => addOtherChoiceToField(field, newVal)}
                        />
                    ) : (
                        <p className="text-sm text-muted-foreground italic">No choices configured</p>
                    )}
                </div>
            );
        }

        // Single-select dropdown
        if (interfaceType === 'select-dropdown') {
            const choices = (field.meta?.options?.choices as Array<{ text: string; value: string }>) || [];
            const allowOther = field.meta?.options?.allowOther as boolean | undefined;
            const allowNone = field.meta?.options?.allowNone as boolean | undefined;
            const customValue = customSelectValues[fieldName] || '';
            const noneSentinel = '__none__';
            const choiceValues = new Set(choices.map((c) => c.value));
            // Ensure value is properly converted to string for Select component
            const selectValue = value === null || value === undefined ? noneSentinel : String(value);
            return (
                <div key={fieldName} className={cn('space-y-2', widthClass)}>
                    <Label htmlFor={fieldName} className="capitalize">
                        {label}
                        {isRequired && <span className="text-destructive ml-1">*</span>}
                    </Label>
                    {choices.length > 0 ? (
                        <div className="space-y-2">
                            <Select
                                key={`${fieldName}-${selectValue}`}
                                value={selectValue}
                                onValueChange={(val) => {
                                    if (val === noneSentinel) {
                                        updateField(fieldName, null);
                                    } else {
                                        updateField(fieldName, val);
                                    }
                                }}
                                disabled={isReadonly}
                            >
                                <SelectTrigger>
                                    <SelectValue placeholder="Select..." />
                                </SelectTrigger>
                                <SelectContent>
                                    {allowNone && (
                                        <SelectItem value={noneSentinel}>None</SelectItem>
                                    )}
                                    {choices.map((choice) => (
                                        <SelectItem key={choice.value} value={choice.value}>
                                            {choice.text}
                                        </SelectItem>
                                    ))}
                                    {allowOther && customValue && !choiceValues.has(customValue) && (
                                        <SelectItem value={customValue}>
                                            Add "{customValue}"
                                        </SelectItem>
                                    )}
                                </SelectContent>
                            </Select>
                            {allowOther && !isReadonly && (
                                <div className="flex gap-2">
                                    <Input
                                        placeholder="Add other..."
                                        value={customValue}
                                        onChange={(e) => setCustomSelectValues((prev) => ({ ...prev, [fieldName]: e.target.value }))}
                                    />
                                    <Button
                                        type="button"
                                        variant="outline"
                                        onClick={() => {
                                            if (customValue) {
                                                updateField(fieldName, customValue);
                                                // Also persist the new choice to field settings
                                                addOtherChoiceToField(field, customValue);
                                                // Clear the custom input
                                                setCustomSelectValues((prev) => ({ ...prev, [fieldName]: '' }));
                                            }
                                        }}
                                    >
                                        Add
                                    </Button>
                                    {allowNone && (
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            onClick={() => updateField(fieldName, null)}
                                        >
                                            Clear
                                        </Button>
                                    )}
                                </div>
                            )}
                            {allowNone && !allowOther && !isReadonly && (
                                <Button
                                    type="button"
                                    variant="ghost"
                                    onClick={() => updateField(fieldName, null)}
                                >
                                    Clear selection
                                </Button>
                            )}
                            {allowNone && isReadonly && (value === null || value === undefined) && (
                                <p className="text-sm text-muted-foreground">None</p>
                            )}
                        </div>
                    ) : (
                        <p className="text-sm text-muted-foreground italic">No choices configured</p>
                    )}
                </div>
            );
        }

        // Markdown editor
        if (interfaceType === 'input-rich-text-md' || field.meta?.options?.markdown) {
            const defaultView = (field.meta?.options?.view as 'edit' | 'preview' | undefined) || (isReadonly ? 'preview' : 'edit');
            return (
                <div key={fieldName} className={cn('space-y-2', widthClass)}>
                    <Label htmlFor={fieldName} className="capitalize">
                        {label}
                        {isRequired && <span className="text-destructive ml-1">*</span>}
                    </Label>
                    <MarkdownEditor
                        value={String(value ?? '')}
                        onChange={(val) => updateField(fieldName, val)}
                        disabled={isReadonly}
                        defaultView={defaultView}
                    />
                </div>
            );
        }

        // URL interface
        if (interfaceType === 'input-url') {
            return (
                <UrlFieldRenderer
                    key={fieldName}
                    fieldName={fieldName}
                    label={label}
                    value={String(value ?? '')}
                    placeholder={field.meta?.options?.placeholder as string | undefined}
                    helperText={helperText}
                    isRequired={isRequired}
                    isReadonly={isReadonly}
                    widthClass={widthClass}
                    onChange={(v) => updateField(fieldName, v)}
                    collection={collection}
                    itemId={isNew ? undefined : id}
                />
            );
        }

        // DateTime interface
        if (interfaceType === 'datetime') {
            const formatOption = field.meta?.options?.format as string | undefined;
            const displayFormat = formatOption === 'short'
                ? 'P p'
                : formatOption === 'long'
                    ? 'PPP p'
                    : formatOption;
            return (
                <div key={fieldName} className={cn('space-y-2', widthClass)}>
                    <Label htmlFor={fieldName} className="capitalize">
                        {label}
                        {isRequired && <span className="text-destructive ml-1">*</span>}
                    </Label>
                    <DateTimePicker
                        value={value as string | null}
                        onChange={(val) => updateField(fieldName, val)}
                        type="datetime"
                        displayFormat={displayFormat}
                        disabled={isReadonly}
                    />
                </div>
            );
        }

        // Textarea/multiline interface
        if (interfaceType === 'input-multiline' || interfaceType === 'textarea') {
            return (
                <div key={fieldName} className={cn('space-y-2', widthClass)}>
                    <Label htmlFor={fieldName} className="capitalize">
                        {label}
                        {isRequired && <span className="text-destructive ml-1">*</span>}
                    </Label>
                    <div className="relative">
                        <Textarea
                            id={fieldName}
                            value={String(value ?? '')}
                            onChange={(e) => {
                                if (isReadonly) return;
                                let newValue = e.target.value;
                                if (field.meta?.options?.trim) {
                                    newValue = newValue.trim();
                                }
                                updateField(fieldName, newValue);
                            }}
                            onBlur={(e) => {
                                if (isReadonly) return;
                                if (field.meta?.options?.clear && e.target.value === '') {
                                    updateField(fieldName, '');
                                }
                            }}
                            disabled={isReadonly}
                            rows={4}
                            maxLength={field.meta?.options?.softLength as number | undefined}
                            className={cn(
                                field.meta?.options?.font === 'monospace' && 'font-mono',
                                field.meta?.options?.font === 'serif' && 'font-serif'
                            )}
                        />
                        {typeof field.meta?.options?.softLength === 'number' && field.meta.options.softLength > 0 && (
                            <div className="absolute right-2 bottom-2 text-xs text-muted-foreground pointer-events-none">
                                {String(value ?? '').length}/{field.meta.options.softLength}
                            </div>
                        )}
                    </div>
                </div>
            );
        }

        // ===== TYPE-BASED RENDERING (fallback when no interface specified) =====

        // Textarea for text type
        if (fieldType === 'text') {
            return (
                <div key={fieldName} className={cn('space-y-2', widthClass)}>
                    <Label htmlFor={fieldName} className="capitalize">
                        {label}
                        {isRequired && <span className="text-destructive ml-1">*</span>}
                    </Label>
                    <div className="relative">
                        <Textarea
                            id={fieldName}
                            value={String(value ?? '')}
                            onChange={(e) => {
                                if (isReadonly) return;
                                let newValue = e.target.value;
                                // Apply trim if enabled
                                if (field.meta?.options?.trim) {
                                    newValue = newValue.trim();
                                }
                                updateField(fieldName, newValue);
                            }}
                            onBlur={(e) => {
                                if (isReadonly) return;
                                // Handle clear option - save empty string instead of null
                                if (field.meta?.options?.clear && e.target.value === '') {
                                    updateField(fieldName, '');
                                }
                            }}
                            disabled={isReadonly}
                            rows={4}
                            maxLength={field.meta?.options?.softLength as number | undefined}
                            className={cn(
                                field.meta?.options?.font === 'monospace' && 'font-mono',
                                field.meta?.options?.font === 'serif' && 'font-serif'
                            )}
                        />
                        {typeof field.meta?.options?.softLength === 'number' && field.meta.options.softLength > 0 && (
                            <div className="absolute right-2 bottom-2 text-xs text-muted-foreground pointer-events-none">
                                {String(value ?? '').length}/{field.meta.options.softLength}
                            </div>
                        )}
                    </div>
                    {helperText && (
                        <p className="text-xs text-muted-foreground">{helperText}</p>
                    )}
                </div>
            );
        }

        // JSON fields (only if no interface specified that handles JSON like select-multiple-dropdown)
        if (fieldType === 'json') {
            const jsonValue = value;
            const displayValue = typeof jsonValue === 'object'
                ? JSON.stringify(jsonValue, null, 2)
                : String(jsonValue ?? '');
            return (
                <div key={fieldName} className={cn('space-y-2', widthClass)}>
                    <Label htmlFor={fieldName} className="capitalize">
                        {label}
                    </Label>
                    <Textarea
                        id={fieldName}
                        value={displayValue}
                        onChange={(e) => {
                            if (isReadonly) return;
                            try {
                                const parsed = JSON.parse(e.target.value);
                                updateField(fieldName, parsed);
                            } catch {
                                updateField(fieldName, e.target.value);
                            }
                        }}
                        className="font-mono text-sm"
                        rows={6}
                        disabled={isReadonly}
                    />
                </div>
            );
        }

        // Number fields
        if (['integer', 'biginteger', 'float', 'decimal'].includes(fieldType)) {
            return (
                <div key={fieldName} className={cn('space-y-2', widthClass)}>
                    <Label htmlFor={fieldName} className="capitalize">
                        {label}
                        {isRequired && <span className="text-destructive ml-1">*</span>}
                    </Label>
                    <Input
                        id={fieldName}
                        type="number"
                        value={value != null ? String(value) : ''}
                        onChange={(e) => {
                            const num = e.target.value === '' ? null : Number(e.target.value);
                            updateField(fieldName, num);
                        }}
                        disabled={isReadonly}
                    />
                    {helperText && (
                        <p className="text-xs text-muted-foreground">{helperText}</p>
                    )}
                </div>
            );
        }

        // Date/DateTime fields by type
        if (['date', 'datetime', 'timestamp'].includes(fieldType)) {
            const pickerType = fieldType === 'date' ? 'date' : 'datetime';
            const formatOption = field.meta?.options?.format as string | undefined;
            const displayFormat = formatOption === 'short'
                ? (pickerType === 'date' ? 'P' : 'P p')
                : formatOption === 'long'
                    ? (pickerType === 'date' ? 'PPP' : 'PPP p')
                    : formatOption;
            return (
                <div key={fieldName} className={cn('space-y-2', widthClass)}>
                    <Label htmlFor={fieldName} className="capitalize">
                        {label}
                        {isRequired && <span className="text-destructive ml-1">*</span>}
                    </Label>
                    <DateTimePicker
                        value={value as string | null}
                        onChange={(val) => updateField(fieldName, val)}
                        type={pickerType}
                        displayFormat={displayFormat}
                        disabled={isReadonly}
                    />
                </div>
            );
        }

        // Time fields
        if (fieldType === 'time') {
            return (
                <div key={fieldName} className={cn('space-y-2', widthClass)}>
                    <Label htmlFor={fieldName} className="capitalize">
                        {label}
                        {isRequired && <span className="text-destructive ml-1">*</span>}
                    </Label>
                    <DateTimePicker
                        value={value as string | null}
                        onChange={(val) => updateField(fieldName, val)}
                        type="time"
                        disabled={isReadonly}
                    />
                </div>
            );
        }

        // Alias type with no specific interface - don't render anything
        if (fieldType === 'alias' && !interfaceType) {
            return null;
        }

        // Default: text input
        return (
            <div key={fieldName} className={cn('space-y-2', widthClass)}>
                <Label htmlFor={fieldName} className="capitalize">
                    {label}
                    {isRequired && <span className="text-destructive ml-1">*</span>}
                </Label>
                <div className="relative">
                    <Input
                        id={fieldName}
                        type={field.meta?.options?.masked ? 'password' : 'text'}
                        value={String(value ?? '')}
                        onChange={(e) => {
                            if (isReadonly) return;
                            let newValue = e.target.value;
                            // Apply trim if enabled
                            if (field.meta?.options?.trim) {
                                newValue = newValue.trim();
                            }
                            updateField(fieldName, newValue);
                        }}
                        onBlur={(e) => {
                            if (isReadonly) return;
                            // Handle clear option - save empty string instead of null
                            if (field.meta?.options?.clear && e.target.value === '') {
                                updateField(fieldName, '');
                            }
                        }}
                        disabled={isReadonly}
                        maxLength={field.meta?.options?.softLength as number | undefined}
                        className={cn(
                            field.meta?.options?.font === 'monospace' && 'font-mono',
                            field.meta?.options?.font === 'serif' && 'font-serif'
                        )}
                    />
                    {typeof field.meta?.options?.softLength === 'number' && field.meta.options.softLength > 0 && (
                        <div className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground pointer-events-none">
                            {String(value ?? '').length}/{field.meta.options.softLength}
                        </div>
                    )}
                </div>
                {helperText && (
                    <p className="text-xs text-muted-foreground">{helperText}</p>
                )}
            </div>
        );
    };


    // Loading state
    const isLoading = fieldsLoading || (!isNew && itemLoading);
    const isSaving = createItem.isPending || updateItem.isPending;
    const saveDisabled = isSaving || (!isNew && !hasChanges) || (isNew ? !canCreate : !canUpdate);
    const archiveDisabled = updateItem.isPending || readonlyByPermission;
    const deleteDisabled = deleteItem.isPending || !canDelete;

    if (!collection) {
        return (
            <div className="flex h-full items-center justify-center">
                <div className="text-center">
                    <h2 className="text-xl font-semibold text-foreground">No Collection Selected</h2>
                </div>
            </div>
        );
    }

    return (
        <TooltipProvider>
            <div className="flex h-full flex-col">
                {/* Header */}
                <div className="flex items-center justify-between border-b border-border bg-background px-6 py-4">
                    <div className="flex items-center gap-4">
                        <Button variant="ghost" size="icon" onClick={handleBack}>
                            <ArrowLeft className="h-5 w-5" />
                        </Button>
                        <div>
                            <h1 className="text-xl font-bold text-foreground">
                                {isNew ? 'Create Item' : 'Edit Item'}
                            </h1>
                            <p className="text-sm text-muted-foreground capitalize">
                                {collectionInfo?.meta?.collection || collection}
                                {!isNew && ` / ${id}`}
                            </p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        {!isNew && hasArchive ? (
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <Button
                                        variant="outline"
                                        onClick={() => handleArchiveToggle(isArchived ? 'unarchive' : 'archive')}
                                        disabled={archiveDisabled}
                                    >
                                        {isArchived ? (
                                            <ArchiveRestore className="mr-2 h-4 w-4" />
                                        ) : (
                                            <Archive className="mr-2 h-4 w-4" />
                                        )}
                                        {isArchived ? 'Unarchive' : 'Archive'}
                                    </Button>
                                </TooltipTrigger>
                                {readonlyByPermission && <TooltipContent>Not allowed</TooltipContent>}
                            </Tooltip>
                        ) : (
                            !isNew && (
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <Button
                                            variant="outline"
                                            onClick={() => setDeleteDialogOpen(true)}
                                            disabled={deleteDisabled}
                                        >
                                            <Trash2 className="mr-2 h-4 w-4" />
                                            Delete
                                        </Button>
                                    </TooltipTrigger>
                                    {!canDelete && <TooltipContent>Not allowed</TooltipContent>}
                                </Tooltip>
                            )
                        )}
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <Button
                                    onClick={() => handleSubmit()}
                                    disabled={saveDisabled}
                                >
                                    {isSaving ? (
                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    ) : (
                                        <Save className="mr-2 h-4 w-4" />
                                    )}
                                    {isNew ? 'Create' : 'Save'}
                                </Button>
                            </TooltipTrigger>
                            {((isNew && !canCreate) || (!isNew && !canUpdate)) && <TooltipContent>Not allowed</TooltipContent>}
                        </Tooltip>
                    </div>
                </div>

                {/* Content Area with Sidebar */}
                <div className="flex-1 flex overflow-hidden">
                    {/* Main Content */}
                    <div className="flex-1 overflow-auto p-6">
                        {isLoading ? (
                            <div className="flex h-full items-center justify-center">
                                <Loader2 className="h-8 w-8 animate-spin text-primary" />
                            </div>
                        ) : (
                            <form onSubmit={handleSubmit}>
                                <Card>
                                    <CardHeader>
                                        <CardTitle>Item Details</CardTitle>
                                        <CardDescription>
                                            {isNew
                                                ? 'Fill in the fields below to create a new item'
                                                : 'Update the fields below to modify this item'}
                                        </CardDescription>
                                    </CardHeader>
                                    <CardContent>
                                        <div className="grid grid-cols-2 gap-6">
                                            {editableFields.map(renderField)}
                                        </div>
                                    </CardContent>
                                </Card>
                            </form>
                        )}
                    </div>
                </div>

                {/* Unsaved changes indicator */}
                {hasChanges && (
                    <div className="fixed bottom-4 left-1/2 -translate-x-1/2 rounded-lg border border-border bg-background px-4 py-2 shadow-lg">
                        <div className="flex items-center gap-3">
                            <span className="text-sm text-muted-foreground">You have unsaved changes</span>
                            <div className="flex gap-2">
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={handleDiscard}
                                >
                                    <X className="mr-1 h-4 w-4" />
                                    Discard
                                </Button>
                                <Button
                                    size="sm"
                                    onClick={() => handleSubmit()}
                                    disabled={isSaving || readonlyByPermission}
                                >
                                    {isSaving ? (
                                        <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                                    ) : (
                                        <Check className="mr-1 h-4 w-4" />
                                    )}
                                    Save
                                </Button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Delete Confirmation Dialog */}
                {!hasArchive && (
                    <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
                        <AlertDialogContent>
                            <AlertDialogHeader>
                                <AlertDialogTitle>Delete Item</AlertDialogTitle>
                                <AlertDialogDescription>
                                    Are you sure you want to delete this item? This action cannot be undone.
                                </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction
                                    onClick={handleDelete}
                                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                    disabled={!canDelete || deleteItem.isPending}
                                >
                                    {deleteItem.isPending ? (
                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    ) : (
                                        <Trash2 className="mr-2 h-4 w-4" />
                                    )}
                                    Delete
                                </AlertDialogAction>
                            </AlertDialogFooter>
                        </AlertDialogContent>
                    </AlertDialog>
                )}
            </div>
        </TooltipProvider>
    );
}
